import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useRanger } from 'react-ranger';
import { kFormatter } from '@lib/utils';

// ---------------------------------------------------------

import Select from '@components/select';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  container,
  handle,
  header,
  margin_top,
  tick,
  tick_label,
  track,
  range_slider_select,
  segment,
  select_container
} = styles;

// ---------------------------------------------------------

export const generateRange = (interval, rangeMax, rangeMin, marksSuffix) => {
  const length = (rangeMax - rangeMin) / interval + 1;

  const arr = Array.from({ length }, (_, i) => rangeMin + i * interval);
  if (marksSuffix === ' ac') {
    arr.splice(1, 0, 1, 5, 10);
  }
  return arr;
};

// ---------------------------------------------------------

const RangeSlider = (props) => {
  let {
    anyLabel = 'Any value',
    interval = 5,
    marksPrefix = '',
    marksSuffix = '',
    max = 100,
    min = 0,
    rangeMax = 100,
    rangeMin = 0,
    label,
    removeSlider,
    onChangeMinMax
  } = props;

  const [dragMin, setDragMin] = useState(rangeMin || min);
  const [dragMax, setDragMax] = useState(rangeMax || max);

  useEffect(() => {
    setDragMin(min);
    setDragMax(max);
  }, [min, max]);

  const generateTickLabels = (max) => {
    let arr = [];

    if (max % 8 === 0) {
      for (let i = 0; i < 10; i++) {
        arr.push(max * (i * 0.125));
      }
    } else {
      if (max % 4 === 0) {
        for (let i = 0; i < 5; i++) {
          arr.push(max * (i * 0.25));
        }
      }
    }

    return arr;
  };

  // ---------------------------------------------------------

  const minimum = (selectedOptions) => ({
    anyLabel: anyLabel,
    label: 'Minimum',
    onChange: handleMinChange,
    options: selectedOptions,
    selected: dragMin
  });

  const maximum = (reversedOptions) => ({
    anyLabel: anyLabel,
    label: 'Maximum',
    onChange: handleMaxChange,
    options: reversedOptions,
    selected: dragMax
  });

  const handleMinChange = (minVal) => {
    if (minVal < max) {
      onChangeMinMax({ max, min: minVal });
    }
  };

  const handleMaxChange = (maxVal) => {
    if (maxVal > min) {
      onChangeMinMax({ max: maxVal, min });
    }
  };

  // ---------------------------------------------------------

  const generateOptions = (range, reversed, anyLabel, marksPrefix, marksSuffix) => {
    let options = [];

    for (let i = 0; i < range.length; i++) {
      let object = {};
      if (i === 0 || i === range.length - 1) {
        object = {
          label: anyLabel,
          value: range[i]
        };
      } else {
        object = {
          label:
            anyLabel === 'Any Price'
              ? `$ ${range[i].toLocaleString('en-US')}`
              : `${marksPrefix}${range[i]}${marksSuffix}`,
          value: range[i]
        };
      }
      options.push(object);
    }

    if (reversed) {
      options.reverse();
    }

    options.pop();

    return options;
  };

  // ---------------------------------------------------------

  let range = generateRange(interval, rangeMax, rangeMin, marksSuffix);
  let tickLabels = generateTickLabels(rangeMax);
  let tickLength = tickLabels.length;
  let selectedOptions = generateOptions(range, false, anyLabel, marksPrefix, marksSuffix, max);
  let reversedOptions = generateOptions(range, true, anyLabel, marksPrefix, marksSuffix, max);

  // ---------------------------------------------------------

  const { getTrackProps, segments, ticks, handles } = useRanger({
    max: rangeMax,
    min: rangeMin,
    onChange: (e) => {
      if (e[0] >= e[1]) return;
      onChangeMinMax({ max: e[1], min: e[0] });
    },
    onDrag: (e) => {
      if (e[0] < e[1] && e[0] !== min) setDragMin(e[0]);
      if (e[0] < e[1] && e[1] !== max) setDragMax(e[1]);
    },
    steps: range,
    ticks: tickLabels,
    values: [dragMin, dragMax]
  });

  return (
    <div className={container}>
      {label && <span className={header}>{label}</span>}
      {!removeSlider && (
        <>
          <div className={track} {...getTrackProps()}>
            {ticks.map(({ value, getTickProps }, index) => (
              <div className={tick} key={`tick${index}`} {...getTickProps()}>
                {ticks[tickLength - 1].value % 8 === 0 ? (
                  index % 2 === 0 && (
                    <div className={tick_label}>
                      {marksPrefix}
                      {value < 999 || value < -999 ? value : kFormatter(value)}
                      {marksSuffix}
                    </div>
                  )
                ) : (
                  <div className={tick_label}>
                    {marksPrefix}
                    {value < 999 || value < -999 ? value : kFormatter(value)}
                    {marksSuffix}
                  </div>
                )}
              </div>
            ))}
            {segments.map(({ getSegmentProps }, i) => (
              <div
                className={(i === 1 && segment) || ''}
                key={`segment${i}`}
                {...getSegmentProps()}
                index={i}
              />
            ))}
            {handles.map(({ getHandleProps }, i) => (
              <button
                {...getHandleProps({
                  style: {
                    appearance: 'none',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none'
                  }
                })}
                key={`handle${i}`}
              >
                <div className={handle} />
              </button>
            ))}
          </div>
        </>
      )}

      <div className={[select_container, !removeSlider && margin_top].filter((e) => !!e).join(' ')}>
        <Select className={range_slider_select} {...minimum(selectedOptions)} />
        <Select className={range_slider_select} {...maximum(reversedOptions)} />
      </div>
    </div>
  );
};

RangeSlider.propTypes = {
  /**
   * Specifies the label for the any option in the select dropdown
   */
  anyLabel: PropTypes.string.isRequired,

  /**
   * Specifies intervals in that the range sliders values progresses by
   */
  interval: PropTypes.number,

  /**
   * An optional label to appear above the slider/dropdowns
   */
  label: PropTypes.string,

  /**
   * Adds a prefix to the mark numbers
   */
  marksPrefix: PropTypes.string,

  /**
   * Adds a suffix to the mark numbers
   */
  marksSuffix: PropTypes.string,

  /**
   * Specifies the current max value
   */
  max: PropTypes.number.isRequired,

  /**
   * Specifies the current min value
   */
  min: PropTypes.number,

  /**
   * onChange function to trigger when the value in the min/max select is changed
   * returns an object in the shape of { min: Int, max: Int }
   */
  onChangeMinMax: PropTypes.func.isRequired,

  /**
   * Specifies the end of the range for the slider. Ensure this number is larger
   * or equal to the 'max' prop.
   */
  rangeMax: PropTypes.number,

  /**
   * Specifies the start of the range for the slider. Make sure this number is smaller
   * than or equal to the 'min' prop.
   */
  rangeMin: PropTypes.number,

  /**
   * Specifies if the slider should be used
   */
  removeSlider: PropTypes.bool
};

export default RangeSlider;
